import React, { useState, useEffect } from 'react';
import { userService } from '../services/userService';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import ResetPasswordModal from '../components/ResetPasswordModal';

import DepartmentSelect from '../components/DepartmentSelect';
const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const { user: currentUser, isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'employee',
    phone: '',
    employee_id: '',
    position: '',
    department: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAll();
      setUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // F1/F2: the edit PUT never carries a password — password changes go
        // through the "Reset password" action (the admin reset route).
        const { password, ...profile } = formData;
        await userService.update(editingUser.id, profile);
        toast.success('User updated!');
      } else {
        await userService.create(formData);
        toast.success('User created!');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      phone: user.phone || '',
      employee_id: user.employee_id || '',
      position: user.position || '',
      department: user.department || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.delete(userId);
        toast.success('User deleted');
        fetchUsers();
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'employee',
      phone: '',
      employee_id: '',
      position: '',
      department: ''
    });
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'general_manager': return 'role-general-manager';
      case 'manager': return 'role-manager';
      case 'driver': return 'role-driver';
      case 'dispatcher': return 'role-dispatcher';
      default: return 'role-employee';
    }
  };

  const formatRoleName = (role) => {
    switch (role) {
      case 'general_manager': return 'General Manager';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>User Management</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add User
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Position</th>
                <th>Department</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {formatRoleName(user.role)}
                    </span>
                  </td>
                  <td>{user.position || '-'}</td>
                  <td>{user.department || '-'}</td>
                  <td>
                    <span className={user.is_active ? 'status-active' : 'status-inactive'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-small" onClick={() => handleEdit(user)}>Edit</button>
                    {isAdmin && (
                      <button className="btn-small" onClick={() => setResetTarget(user)}>Reset password</button>
                    )}
                    {user.id !== currentUser?.id && (
                      <button className="btn-small btn-danger" onClick={() => handleDelete(user.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input 
                    type="text" 
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input 
                    type="email" 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="form-row">
                {!editingUser && (
                  <div className="form-group">
                    <label>Password *</label>
                    <input 
                      type="password" 
                      value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Role *</label>
                  <select 
                    value={formData.role}
                    onChange={e => setFormData({...formData, role: e.target.value})}
                  >
                    <option value="employee">Employee</option>
                    <option value="driver">Driver</option>
                    <option value="dispatcher">Dispatcher</option>
                    <option value="accounting">Accounting</option>
                    <option value="manager">Manager</option>
                    <option value="general_manager">General Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input 
                    type="tel" 
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Employee ID</label>
                  <input 
                    type="text" 
                    value={formData.employee_id}
                    onChange={e => setFormData({...formData, employee_id: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Position</label>
                  <input 
                    type="text" 
                    value={formData.position}
                    onChange={e => setFormData({...formData, position: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <DepartmentSelect
                    value={formData.department}
                    onChange={e => setFormData({...formData, department: e.target.value})}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Update' : 'Create'} User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          isSelf={resetTarget.id === currentUser?.id}
          onClose={() => setResetTarget(null)}
        />
      )}
    </div>
  );
};

export default Users;
